import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { type ReactElement, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { useBreadcrumbOverrides } from '../../layout/navigation/BreadcrumbOverridesContext';
import { useGetTeamQuery } from '../../store/api/apiSlice';
import { type SessionTranscriptItem, useGetSessionTranscriptsQuery } from './api/sessionApi';
import { getMemberDisplayName } from './types';

export const IMAGES_NOTE = 'Images are not displayed and not used for artifacts.';
const NO_TRANSCRIPT_MESSAGE = 'No transcript is available for this session.';

// Images are dropped because the note above promises they are not shown. The
// API removes embedded images but leaves linked ones in the text.
const DISALLOWED_ELEMENTS = ['img'];

// Matches the member transcript page: keep the transcript's line breaks and
// show timestamp headings as bold lines.
const TRANSCRIPT_TEXT_SX = {
  color: '#2B2E2B',
  fontSize: 14,
  lineHeight: 1.43,
  wordBreak: 'break-word',
  '& p': { m: '0 0 16px', whiteSpace: 'pre-wrap' },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontSize: 14,
    fontWeight: 700,
    m: '24px 0 16px',
    whiteSpace: 'pre-wrap',
  },
  '& ul, & ol': { m: '0 0 16px', pl: 3 },
  '& li': { mb: 1, whiteSpace: 'pre-wrap' },
} as const;

function formatSessionDate(value?: string): string {
  if (!value) {
    return 'Session transcript';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Session transcript';
  }
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function transcriptStateMessage(item: SessionTranscriptItem): string | null {
  switch (item.content_status) {
    case 'ready':
      return item.content_markdown?.trim() ? null : 'No transcript was recorded in this file.';
    case 'pending':
      return "This transcript isn't available yet.";
    default:
      return "Moments couldn't read this transcript.";
  }
}

function TranscriptFile({
  item,
  index,
  showHeading,
}: {
  item: SessionTranscriptItem;
  index: number;
  showHeading: boolean;
}): ReactElement {
  const message = transcriptStateMessage(item);
  const heading = item.display_name?.trim() || `Transcript ${index + 1}`;

  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {showHeading ? (
        <Typography component="h2" sx={{ color: '#2B2E2B', fontSize: 17, fontWeight: 700 }}>
          {heading}
        </Typography>
      ) : null}
      {message ? (
        <Typography sx={{ color: '#6A746D', fontSize: 14 }}>{message}</Typography>
      ) : (
        <Box data-testid="coach-transcript-content" sx={TRANSCRIPT_TEXT_SX}>
          <ReactMarkdown
            disallowedElements={DISALLOWED_ELEMENTS}
            rehypePlugins={[rehypeSanitize]}
            remarkPlugins={[remarkGfm]}
          >
            {item.content_markdown ?? ''}
          </ReactMarkdown>
        </Box>
      )}
    </Box>
  );
}

export default function CoachSessionTranscriptPage(): ReactElement {
  const { personId, sessionId } = useParams<{ personId: string; sessionId: string }>();
  // The session list opens this page in a new tab (MEN-5260), so the member's
  // name comes from the same team read the list uses, not from router state.
  const { data: teamData } = useGetTeamQuery();
  const memberLabel = useMemo(() => {
    const member = teamData?.members?.find((teamMember) => teamMember.person.id === personId);
    return member ? getMemberDisplayName(member) : undefined;
  }, [personId, teamData?.members]);
  const { setBreadcrumbOverrides } = useBreadcrumbOverrides();
  const { data, error, isLoading } = useGetSessionTranscriptsQuery(sessionId ?? '', {
    skip: !sessionId,
  });

  useEffect(() => {
    if (!personId) return;
    setBreadcrumbOverrides({
      memberContext: { memberId: personId, memberLabel: memberLabel ?? 'Member' },
    });
  }, [memberLabel, personId, setBreadcrumbOverrides]);

  const items = data?.items ?? [];

  return (
    <Box sx={{ mx: 'auto', maxWidth: 880, px: { xs: 2, md: 4 }, py: 4 }}>
      <Typography component="h1" sx={{ color: '#2B2E2B', fontSize: 24, fontWeight: 700, mb: 1 }}>
        {formatSessionDate(data?.session.planned_start_at)}
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        {IMAGES_NOTE}
      </Alert>
      <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 4, p: { xs: 2, md: 4 } }}>
        {isLoading ? (
          <CircularProgress aria-label="Loading transcript" size={24} />
        ) : error || items.length === 0 ? (
          <Typography sx={{ color: '#6A746D', fontSize: 14 }}>{NO_TRANSCRIPT_MESSAGE}</Typography>
        ) : (
          items.map((item, index) => (
            <TranscriptFile
              index={index}
              item={item}
              key={item.attachment_id}
              showHeading={items.length > 1}
            />
          ))
        )}
      </Paper>
    </Box>
  );
}
